import { useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicGenerator } from '../hooks/usePublicGenerator';
import { GeneratorRenderer } from '../components/renderer/GeneratorRenderer';
import { createDefaultConfig } from '../lib/defaultConfig';

/**
 * Page publique d'un générateur : /app/g/:orgSlug/:genSlug
 * Accessible sans authentification. Fonctionne grâce aux RLS de `public_access.sql`.
 *
 * Supporte le mode embed via `?embed=1` :
 *   - masque le footer légal (à afficher côté site hôte)
 *   - réduit les paddings/ombres pour un rendu inline
 *   - publie en continu la hauteur du contenu au parent via postMessage
 *     (événement `quotio:resize`), pour permettre l'auto-resize de l'iframe.
 */
export function PublicGeneratorPage() {
    const { orgSlug, genSlug } = useParams<{ orgSlug: string; genSlug: string }>();
    const [searchParams] = useSearchParams();
    const { loading, error, generator } = usePublicGenerator(orgSlug, genSlug);

    const isEmbed = useMemo(() => {
        const v = searchParams.get('embed');
        return v === '1' || v === 'true';
    }, [searchParams]);

    const shellRef = useRef<HTMLDivElement>(null);

    // Ajoute une classe <body> pour aider les styles globaux en mode embed
    useEffect(() => {
        if (!isEmbed) return;
        document.body.classList.add('quotio-embed');
        return () => document.body.classList.remove('quotio-embed');
    }, [isEmbed]);

    // Publication en continu de la hauteur du contenu au parent (iframe auto-resize)
    useEffect(() => {
        if (!isEmbed) return;
        if (typeof window === 'undefined') return;

        const postHeight = () => {
            const el = shellRef.current ?? document.body;
            const h = Math.max(
                el.scrollHeight,
                el.offsetHeight,
                document.documentElement.scrollHeight,
            );
            try {
                window.parent.postMessage(
                    { type: 'quotio:resize', height: h },
                    '*',
                );
            } catch {
                /* cross-origin parent : on ignore */
            }
        };

        // Initial + observation des changements de taille
        postHeight();
        const ro = new ResizeObserver(() => postHeight());
        if (shellRef.current) ro.observe(shellRef.current);
        ro.observe(document.body);
        window.addEventListener('load', postHeight);
        window.addEventListener('resize', postHeight);

        // Ping "ready" dès qu'on sait qu'on est rendu
        try {
            window.parent.postMessage({ type: 'quotio:ready' }, '*');
        } catch {
            /* ignore */
        }

        return () => {
            ro.disconnect();
            window.removeEventListener('load', postHeight);
            window.removeEventListener('resize', postHeight);
        };
    }, [isEmbed, loading]);

    const classes = `public-gen-page${isEmbed ? ' is-embed' : ''}`;

    if (loading) {
        return (
            <div className={classes}>
                <div className="public-gen-shell" ref={shellRef}>
                    <div className="app-loader">
                        <div className="spinner" aria-hidden />
                        <p>Chargement du générateur…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !generator) {
        return (
            <div className={classes}>
                <div className="public-gen-shell" ref={shellRef}>
                    <div className="public-gen-error">
                        <h1>Générateur indisponible</h1>
                        <p>{error ?? "Ce lien ne semble pas valide."}</p>
                        {!isEmbed && (
                            <a href="/" className="btn-primary">Retour à l'accueil</a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const config =
        generator.config ??
        createDefaultConfig({
            companyName: generator.org.name,
            industry: generator.industry,
        });

    return (
        <div className={classes}>
            <div className="public-gen-shell" ref={shellRef}>
                <GeneratorRenderer
                    config={config}
                    showResetButton
                    generatorId={generator.id}
                    organizationId={generator.organization_id}
                />
                {!isEmbed && (
                    <nav
                        className="public-gen-legal"
                        aria-label="Informations légales"
                    >
                        <a href="/conditions-utilisation.html">Conditions d'utilisation</a>
                        <span aria-hidden="true">·</span>
                        <a href="/politique-confidentialite.html">Confidentialité</a>
                        <span aria-hidden="true">·</span>
                        <a href="/cookies.html">Cookies</a>
                        <span aria-hidden="true">·</span>
                        <a href="/mentions-legales.html">Mentions légales</a>
                    </nav>
                )}
            </div>
        </div>
    );
}
